import { DashboardState, ESdChangeReason, IConditionModel, IValidation } from "@kuika/kuika-cl-model";
import { ConditionSetType } from "@kuika/kuika-cl-model/src/models/builder.dtos";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaDropdownMenu from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaOverflowText from "../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import { Comparison } from "./condition";
import { ValidationCondition } from "./validation-condition";

export interface IValidationPanelState {
  isMenuVisible: boolean;
}

export interface IValidationPanelProps {
  validation: IValidation;
  onUpdateValidation: (validation: IValidation, changeReason?: ESdChangeReason | undefined) => void;
  onDeleteValidation: (validation: IValidation) => void;
}

class ValidationPanel extends PureSdComponent<
  IValidationPanelProps & IWithSDProps & IWithContextProp,
  IValidationPanelState
> {
  constructor(props: IValidationPanelProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      isMenuVisible: false
    };
  }

  getSelectedKuikaIds = () => {
    if (
      this.props.sd.dashboardState === DashboardState.dataDiagramScheme ||
      this.props.sd.dashboardState === DashboardState.workFlow
    ) {
      return ["-1"];
    }
    return this.props.sd.selectedKuikaIds;
  };

  handleCondition = (condition: IConditionModel, changeReason?: ESdChangeReason | undefined) => {
    const { validation } = this.props;
    validation.condition = condition;
    this.props.onUpdateValidation(validation, changeReason);
  };

  handleValidationCondition = (validationCondition?: IConditionModel, changeReason?: ESdChangeReason | undefined) => {
    const { validation } = this.props;
    validation.validationCondition = validationCondition;
    this.props.onUpdateValidation(validation, changeReason);
  };

  handleMessageOnChange = (e: any) => {
    const { validation } = this.props;
    validation.message = e.target.value;
    this.props.onUpdateValidation(validation);
  };

  handleKeepPanelVisibility = () => {
    if (this.props.validation.isExpandedPanel === false) {
      this.onExpand();
    } else {
      this.onCollapse();
    }
  };

  handleMenuVisibleChange = () => {
    this.setState({ isMenuVisible: !this.state.isMenuVisible });
  };

  handleMenuClick = (key) => {
    const { validation } = this.props;
    if (key === "1") {
      // Add validation condition
      const blankCondition: IConditionModel = {
        conditionSet: {
          conditions: [],
          type: ConditionSetType.and
        }
      } as IConditionModel;
      validation.validationCondition = blankCondition;
      this.setState({ isMenuVisible: false }, () => {
        this.props.onUpdateValidation(validation);
      });
    } else if (key === "2") {
      // Remove validation condition
      this.setState({ isMenuVisible: false }, () => {
        validation.validationCondition = undefined;
        this.props.onUpdateValidation(validation);
      });
    } else if (key === "3") {
      // Remove validation
      this.props.onDeleteValidation(validation);
    }
  };

  updateValidationIsExpandedPanel = (isExpandedPanel: boolean) => {
    const validation = _.cloneDeep(this.props.validation);
    validation.isExpandedPanel = isExpandedPanel;
    this.props.onUpdateValidation(validation);
  };

  changeCollapse = () => {
    if (this.props.validation.isExpandedPanel === false) {
      this.onExpand();
    } else {
      this.onCollapse();
    }
  };

  onCollapse = () => {
    this.updateValidationIsExpandedPanel(false);
  };

  onExpand = () => {
    this.updateValidationIsExpandedPanel(true);
  };

  getActiveKey = (): string => {
    if (this.props.validation.isExpandedPanel === true) {
      return "1";
    }
    return "";
  };

  render() {
    if (!this.props.validation) {
      return <></>;
    }

    const dropdownOptions = [
      this.props.validation.validationCondition === undefined
        ? {
            key: "1",
            label: "Add Condition",
            onClick: () => this.handleMenuClick("1")
          }
        : {
            key: "2",
            label: "Remove Condition",
            onClick: () => this.handleMenuClick("2")
          },
      {
        key: "3",
        label: "Remove",
        onClick: () => this.handleMenuClick("3"),
        danger: true
      }
    ];

    return (
      <div data-testid="validationPanel">
        <KuikaCollapsePanel
          onChange={this.changeCollapse}
          activeKey={this.getActiveKey()}
          iconPlacement="right"
          className="kuika-right-panel__collapse kuika-right-panel__collapse--boxed kuika-right-panel__collapse--validation"
          items={[
            {
              key: "1",
              label: (
                <div
                  className={OVERFLOW_REVEAL_HOST_CLASS}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}
                >
                  <KuikaTextMedium style={{ display: "block", minWidth: 0 }}>
                    <KuikaOverflowText>Validation</KuikaOverflowText>
                  </KuikaTextMedium>
                  <div
                    style={{ flexShrink: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <KuikaDropdownMenu
                      setOpen={this.handleMenuVisibleChange}
                      open={this.state.isMenuVisible}
                      options={dropdownOptions}
                      trigger="click"
                    >
                      {(open) => (
                        <KuikaButton type="tertiary" size="md" isIcon color="neutral" active={open}>
                          <KuikaSvg name="dots-vertical" type="styling-panel-icons" width={16} height={16} />
                        </KuikaButton>
                      )}
                    </KuikaDropdownMenu>
                  </div>
                </div>
              ),
              children: (
                <div className="kuika-right-panel__collapse-body">
                  <Comparison
                    condition={this.props.validation.condition}
                    onChange={this.handleCondition}
                    conditionType="validation"
                  />
                  <>
                    <div className="kuika-right-panel__row" style={{ marginTop: 15, marginBottom: 15 }}>
                      <div className="kuika-right-panel__col">
                        <KuikaText>Invalid Message</KuikaText>
                      </div>
                    </div>
                    <div style={{ marginTop: "20px" }}>
                      <KuikaInput
                        type="textarea"
                        placeholder="Invalid Message..."
                        data-testid="validationInvalidMessageInput"
                        defaultValue={this.props.validation.message || ""}
                        onChange={(e) => this.handleMessageOnChange(e)}
                        autoSize={{ minRows: 3, maxRows: 5 }}
                        autoFocus
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                  </>
                  <ValidationCondition
                    condition={this.props.validation.validationCondition}
                    onChange={this.handleValidationCondition}
                  />
                  <div className="kuika_styling_typography_footer" style={{ padding: "4px 6px" }}>
                    <KuikaText>Ignore If Not Visible</KuikaText>
                    <KuikaSwitch
                      checked={this.props.validation.ignoreIfNotVisible}
                      onChange={(checked) => {
                        const { validation } = this.props;
                        validation.ignoreIfNotVisible = checked;
                        this.props.onUpdateValidation(validation);
                      }}
                      size="sm"
                      style={{ marginRight: 8 }}
                    />
                  </div>
                  <div className="kuika_styling_typography_footer" style={{ padding: "4px 6px" }}>
                    <KuikaButton type="text" size="sm" onClick={this.changeCollapse}>
                      Close
                    </KuikaButton>
                  </div>
                </div>
              )
            }
          ]}
        />
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.UpdateValidation ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged
    ) {
      return true;
    }
    return false;
  };
}

const validationPanel = withContext<IValidationPanelProps>(withSD<IValidationPanelProps>(ValidationPanel));
export { validationPanel as ValidationPanel };
